import React, { useMemo, useState } from 'react';
import api from '@/lib/api';
import { Loader2, AlertTriangle, Check } from 'lucide-react';

/**
 * Loonbasis-wizard: loopt de medewerkers zonder bruikbare loonbasis langs.
 * Schrijft via het bestaande employee-update-endpoint; leidt niets af en vult
 * niets voor behalve wat er al staat.
 */
export function LoonbasisWizard({ groups, onClose, onSaved }) {
  const queue = useMemo(() => ([
    ...(groups?.oproep_zonder_uurloon || []),
    ...(groups?.vast_zonder_loonbasis || []),
  ]), [groups]);

  const [idx, setIdx] = useState(0);
  const [uurloon, setUurloon] = useState('');
  const [bruto, setBruto] = useState('');
  const [periode, setPeriode] = useState('per_maand');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState([]);

  const emp = queue[idx];

  const next = () => {
    setUurloon(''); setBruto(''); setPeriode('per_maand'); setError('');
    setIdx((i) => i + 1);
  };

  const save = async () => {
    setBusy(true); setError('');
    try {
      const patch = {};
      if (uurloon) patch.uurloon = parseFloat(uurloon);
      if (bruto) { patch.brutosalaris = parseFloat(bruto); patch.salarisperiode = periode; }
      if (Object.keys(patch).length === 0) {
        setError('Vul een uurloon of een brutosalaris in');
        setBusy(false);
        return;
      }
      // Het employee-update-endpoint vereist optimistic locking: haal de actuele
      // versie op en stuur die als If-Match mee.
      const { data: current } = await api.get(`/employees/${emp.id}`);
      await api.put(`/employees/${emp.id}`, patch, {
        headers: { 'If-Match': `"${emp.id}-${current.version ?? 0}"` },
      });
      setDone((d) => [...d, emp.personeelsnummer]);
      onSaved?.();
      next();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="loonbasis-wizard">
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Loonbasis aanvullen</h3>
          <span className="text-xs text-slate-500" data-testid="wizard-progress">
            {Math.min(idx + 1, queue.length)} / {queue.length}
          </span>
        </div>

        {!emp ? (
          <div className="mt-4" data-testid="wizard-klaar">
            <p className="flex items-center gap-2 text-sm text-slate-700">
              <Check size={16} className="text-green-600" />
              Klaar — {done.length} medewerker(s) bijgewerkt.
            </p>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={onClose} data-testid="wizard-close"
                className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90">
                Sluiten
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm font-medium text-slate-900" data-testid="wizard-emp-name">{emp.name}</p>
            <p className="text-xs text-slate-500">
              #{emp.personeelsnummer} · {emp.location_name} · {emp.contract_type}
              {emp.contract_hours ? ` · ${emp.contract_hours}u` : ''} — {emp.mist}
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600">
                  Uurloon {emp.contract_type === 'oproep' ? '(leidend bij oproep)' : ''}
                </label>
                <input
                  type="number" step="0.01" value={uurloon}
                  onChange={(e) => setUurloon(e.target.value)}
                  data-testid="wizard-uurloon"
                  className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                />
              </div>
              {emp.contract_type !== 'oproep' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-600">Of brutosalaris</label>
                    <input
                      type="number" step="0.01" value={bruto}
                      onChange={(e) => setBruto(e.target.value)}
                      data-testid="wizard-brutosalaris"
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600">Salarisperiode</label>
                    <select
                      value={periode} onChange={(e) => setPeriode(e.target.value)}
                      data-testid="wizard-salarisperiode"
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    >
                      <option value="per_maand">Per maand</option>
                      <option value="per_4_weken">Per 4 weken</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <p className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900"
              data-testid="wizard-loonperiode-warning">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              Loonwijzigingen laten ingaan per nieuwe loonperiode (procesafspraak).
            </p>
            {error && <p className="mt-2 text-xs text-red-600" data-testid="wizard-error">{error}</p>}

            <div className="mt-5 flex justify-between gap-2">
              <button type="button" onClick={onClose} data-testid="wizard-stop"
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
                Stoppen
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={next} data-testid="wizard-skip"
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
                  Overslaan
                </button>
                <button type="button" onClick={save} disabled={busy} data-testid="wizard-save"
                  className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : 'Opslaan & volgende'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
